import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import Combobox from "@/components/Combobox";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Loader2 } from "lucide-react";
import BlocchiScadenze from "@/components/BlocchiScadenze";

const emptyForm = {
  codice_viaggio: "", data_viaggio: "", cliente_id: "", tipologia: "carico",
  autista_id: "", trattore_id: "", rimorchio_id: "", localita_partenza_id: "",
  localita_arrivo_id: "", prodotto_id: "", quantita_litri: "", quantita_kg: "",
  quantita_ton: "", quantita_pezzi: "", numero_scarichi: "", giornata_lavorata: "1",
  numero_ddt: "", data_ddt: "", km: "", ore: "", supplementi: [], subvettore_id: "",
  viaggio_collegato_id: "", stato: "pianificato", note: "",
};

export default function ViaggioForm({ open, onOpenChange, config, initial, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [opts, setOpts] = useState({});
  const [saving, setSaving] = useState(false);
  const [suppCodes, setSuppCodes] = useState([]);
  const [blocchi, setBlocchi] = useState([]);
  const [canOverride, setCanOverride] = useState(false);
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    if (!open) return;
    setMotivo(initial?.override_scadenze?.motivo || "");
    setBlocchi([]);
  }, [open, initial]);

  useEffect(() => {
    if (!open || !(form.autista_id || form.trattore_id || form.rimorchio_id)) {
      setBlocchi([]);
      return;
    }
    const params = { autista_id: form.autista_id || undefined, trattore_id: form.trattore_id || undefined,
      rimorchio_id: form.rimorchio_id || undefined, data_viaggio: form.data_viaggio || undefined };
    api.get("/viaggi/verifica-scadenze", { params }).then((r) => {
      setBlocchi(r.data.blocchi);
      setCanOverride(r.data.can_override);
    }).catch(() => {});
  }, [open, form.autista_id, form.trattore_id, form.rimorchio_id, form.data_viaggio]);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...emptyForm, ...initial, supplementi: initial.supplementi || [] } : emptyForm);
    api.get("/listini/supplementi-codici").then((r) => setSuppCodes(r.data)).catch(() => {});
    Promise.all([
      api.get("/clienti/options"),
      api.get("/autisti/options"),
      api.get("/veicoli/options"),
      api.get("/localita/options"),
      api.get("/prodotti/options"),
      api.get("/subvettori/options"),
      api.get("/viaggi", { params: { page_size: 100 } }),
    ]).then(([cl, au, ve, lo, pr, sv, vg]) => {
      setOpts({
        clienti: cl.data, autisti: au.data, veicoli: ve.data, localita: lo.data,
        prodotti: pr.data, subvettori: sv.data,
        viaggi: vg.data.items
          .filter((v) => v.id !== initial?.id)
          .map((v) => ({ value: v.id, label: `${v.codice_viaggio} · ${formatDate(v.data_viaggio)}` })),
      });
    });
  }, [open, initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.data_viaggio) {
      toast.error("La data del viaggio è obbligatoria");
      return;
    }
    const payload = { ...form };
    delete payload.override_scadenze;
    if (blocchi.length) {
      if (!canOverride) {
        toast.error("Assegnazione bloccata da documenti scaduti");
        return;
      }
      if (!motivo.trim()) {
        toast.error("Inserisci la motivazione per forzare l'assegnazione");
        return;
      }
      payload.override_motivo = motivo.trim();
    }
    ["quantita_litri", "quantita_kg", "quantita_ton", "quantita_pezzi", "numero_scarichi", "giornata_lavorata", "km", "ore"].forEach((k) => {
      payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
    });
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/viaggi/${initial.id}`, payload);
      else await api.post("/viaggi", payload);
      toast.success("Viaggio salvato");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const cb = (field, options, ph, tid) => (
    <Combobox options={options || []} value={form[field]} onChange={(v) => set(field, v)} placeholder={ph} testid={tid} />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="viaggio-form">
        <DialogHeader>
          <DialogTitle className="font-head">{initial?.id ? "Modifica viaggio" : "Nuovo viaggio"}</DialogTitle>
          <DialogDescription>Inserimento rapido: usa Tab per spostarti tra i campi.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Data viaggio *</Label>
              <Input type="date" className="mt-1" value={form.data_viaggio || ""} onChange={(e) => set("data_viaggio", e.target.value)} data-testid="viaggio-data" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Codice viaggio <span className="text-slate-400">(vuoto = automatico)</span></Label>
              <Input className="mt-1" value={form.codice_viaggio} onChange={(e) => set("codice_viaggio", e.target.value)} data-testid="viaggio-codice" placeholder="V-260914-001" />
            </div>
            <div>
              <Label className="text-xs">Tipologia</Label>
              <Select value={form.tipologia} onValueChange={(v) => set("tipologia", v)}>
                <SelectTrigger className="mt-1" data-testid="viaggio-tipologia"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config?.tipologie?.map((t) => (<SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Cliente</Label><div className="mt-1">{cb("cliente_id", opts.clienti, "Cliente...", "viaggio-cliente")}</div></div>
            <div><Label className="text-xs">Autista</Label><div className="mt-1">{cb("autista_id", opts.autisti, "Autista...", "viaggio-autista")}</div></div>
            <div><Label className="text-xs">Trattore</Label><div className="mt-1">{cb("trattore_id", opts.veicoli, "Targa trattore...", "viaggio-trattore")}</div></div>
            <div><Label className="text-xs">Rimorchio</Label><div className="mt-1">{cb("rimorchio_id", opts.veicoli, "Targa rimorchio...", "viaggio-rimorchio")}</div></div>
            <div><Label className="text-xs">Località partenza</Label><div className="mt-1">{cb("localita_partenza_id", opts.localita, "Partenza...", "viaggio-partenza")}</div></div>
            <div><Label className="text-xs">Località arrivo</Label><div className="mt-1">{cb("localita_arrivo_id", opts.localita, "Arrivo...", "viaggio-arrivo")}</div></div>
            <div><Label className="text-xs">Prodotto</Label><div className="mt-1">{cb("prodotto_id", opts.prodotti, "Prodotto...", "viaggio-prodotto")}</div></div>
            <div><Label className="text-xs">Sub-vettore (se eseguito da terzi)</Label><div className="mt-1">{cb("subvettore_id", opts.subvettori, "Nessuno...", "viaggio-subvettore")}</div></div>
          </div>

          <BlocchiScadenze blocchi={blocchi} canOverride={canOverride} motivo={motivo} onMotivo={setMotivo} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Litri</Label><Input type="number" step="any" className="mt-1" value={form.quantita_litri} onChange={(e) => set("quantita_litri", e.target.value)} /></div>
            <div><Label className="text-xs">Kg</Label><Input type="number" step="any" className="mt-1" value={form.quantita_kg} onChange={(e) => set("quantita_kg", e.target.value)} /></div>
            <div><Label className="text-xs">Tonnellate</Label><Input type="number" step="any" className="mt-1" value={form.quantita_ton} onChange={(e) => set("quantita_ton", e.target.value)} /></div>
            <div><Label className="text-xs">Pezzi / colli</Label><Input type="number" step="any" className="mt-1" value={form.quantita_pezzi} onChange={(e) => set("quantita_pezzi", e.target.value)} data-testid="viaggio-pezzi" /></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Km percorsi</Label><Input type="number" step="any" className="mt-1" value={form.km} onChange={(e) => set("km", e.target.value)} data-testid="viaggio-km" /></div>
            <div><Label className="text-xs">Nr. scarichi</Label><Input type="number" step="1" className="mt-1" value={form.numero_scarichi} onChange={(e) => set("numero_scarichi", e.target.value)} data-testid="viaggio-scarichi" /></div>
            <div>
              <Label className="text-xs">GG lavorate</Label>
              <Select value={String(form.giornata_lavorata ?? "")} onValueChange={(v) => set("giornata_lavorata", v)}>
                <SelectTrigger className="mt-1" data-testid="viaggio-gg"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — conta giornata</SelectItem>
                  <SelectItem value="0">0 — stesso giorno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Numero DDT</Label><Input className="mt-1" value={form.numero_ddt} onChange={(e) => set("numero_ddt", e.target.value)} data-testid="viaggio-ddt" /></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Data DDT</Label><Input type="date" className="mt-1" value={form.data_ddt || ""} onChange={(e) => set("data_ddt", e.target.value)} /></div>
            <div><Label className="text-xs">Ore (tariffe a ora)</Label><Input type="number" step="any" className="mt-1" value={form.ore} onChange={(e) => set("ore", e.target.value)} data-testid="viaggio-ore" /></div>
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
                <SelectTrigger className="mt-1" data-testid="viaggio-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {config?.stati?.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.tipologia !== "carico" && (
            <div>
              <Label className="text-xs">Viaggio collegato (viaggio carico abbinato)</Label>
              <div className="mt-1">{cb("viaggio_collegato_id", opts.viaggi, "Collega al viaggio di carico...", "viaggio-collegato")}</div>
            </div>
          )}

          {suppCodes.length > 0 && (
            <div>
              <Label className="text-xs">Supplementi applicati (dai listini)</Label>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="viaggio-supplementi">
                {suppCodes.map((s) => {
                  const on = (form.supplementi || []).includes(s.codice);
                  return (
                    <button type="button" key={s.codice} title={s.descrizione} data-testid={`viaggio-supp-${s.codice}`}
                      onClick={() => set("supplementi", on ? form.supplementi.filter((c) => c !== s.codice) : [...(form.supplementi || []), s.codice])}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300 hover:border-sky-400"}`}>
                      {s.codice}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Note</Label>
            <Textarea className="mt-1" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={saving} data-testid="viaggio-submit">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
